import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    Signal,
    signal,
    viewChild,
    WritableSignal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { ModalType } from '@connect-services/client/components/abstract-modal/abstract-modal.component';
import { ConfirmationModal } from '@connect-services/client/components/confirmation-modal/confirmation-modal.component';
import { MainTitleComponent } from '@connect-services/client/components/main-title/main-title.component';
import { NoDataToDisplayComponent } from '@connect-services/client/components/no-data-to-display/no-data-to-display.component';
import { ReadonlyDropdownListComponent } from '@connect-services/client/components/readonly-dropdown-list/readonly-dropdown-list.component';
import { EditOrderComponent } from '@connect-services/client/components/statistics/edit-order/edit-order.component';
import { SelectStatisticsPanelComponent } from '@connect-services/client/components/statistics/select-statistics-panel/select-statistics-panel.component';
import { StatisticGeographicFilterActionsComponent } from '@connect-services/client/components/statistics/statistic-geographic-filter-actions/statistic-geographic-filter-actions.component';
import { StatisticsPanelComponent } from '@connect-services/client/components/statistics/statistics-panel/statistics-panel.component';
import {
    CanDeactivateComponent,
    CanDeactivateDialogConfig,
} from '@connect-services/client/guards/can-deactivate.guard';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { ActionsEnum } from '@connect-services/client/models/panel.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { ExportStatisticsExcelService } from '@connect-services/client/services/export-statistics-excel.service';
import { GeographicFilterService } from '@connect-services/client/services/geographic-filter.service';
import { SelectionService } from '@connect-services/client/services/map/selection/selection.service';
import { MapModal, MapModalService } from '@connect-services/client/services/map-modal.service';
import { SecurityService } from '@connect-services/client/services/security.service';
import {
    LayersAndEntitiesSelection,
    STATISTICS_FILTER_LOCAL_STORAGE_KEY,
    StatisticService,
} from '@connect-services/client/services/statistic.service';
import { ButtonComponent, ButtonType } from '@connect-services/src/lib/components/button/button.component';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { ComputedStatistic, Standard, StandardsService } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';
import {
    defaultPaginationParameters,
    OrderEnum,
    paginationMaxTake,
    PaginationParameters,
} from '@shared/client/models/pagination.model';
import { SnackBarService } from '@shared/client/services/snack-bar.service';

export type EntitiesSelectionFilter = {
    filter: LayersAndEntitiesSelection;
    type: 'entitiesSelection';
};

export type ZoneDelimiterFilter = {
    filter: string;
    type: 'zoneDelimiter';
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ButtonComponent,
        EditOrderComponent,
        LoadingComponent,
        MainTitleComponent,
        NoDataToDisplayComponent,
        ReadonlyDropdownListComponent,
        RessourcesModule,
        SelectComponent,
        SelectStatisticsPanelComponent,
        StatisticGeographicFilterActionsComponent,
        StatisticsPanelComponent,
    ],
    selector: 'app-statistics',
    styleUrl: './statistics.component.scss',
    templateUrl: './statistics.component.html',
})
export class StatisticsComponent implements CanDeactivateComponent {
    public readonly errorWhileFetchingOrganizationStandards: WritableSignal<boolean> = signal(true);
    public readonly isLoading: WritableSignal<boolean> = signal<boolean>(true);
    public readonly isStandardAvailable: WritableSignal<boolean> = signal(false);
    public readonly isStandardSelected: WritableSignal<boolean> = signal(false);
    public readonly standardNameSelected: WritableSignal<string> = signal('');
    public readonly standardOptions: Signal<SelectOption<Standard>[]> = computed(() => {
        return this.standardPerProjectList().map((standard: Standard) => ({
            label: standard.name,
            value: standard,
        }));
    });
    public readonly standardPerProjectList: WritableSignal<Standard[]> = signal([]);

    protected readonly ActionsEnum = ActionsEnum;
    protected readonly ButtonType = ButtonType;
    protected readonly contentLayoutService = inject(ContentLayoutService);
    protected readonly geographicFilterService = inject(GeographicFilterService);
    protected readonly hasUnsavedStatisticOrderChanges = signal(false);
    protected readonly headerPanel = viewChild<ElementRef<HTMLElement>>('headerPanel');
    protected readonly isEditOrderMode: WritableSignal<boolean> = signal(false);
    protected readonly isExportingExcel = signal(false);
    protected readonly isNarrowPanel: WritableSignal<boolean> = signal(false);
    protected readonly RenderEnum = ComputedStatistic.RenderEnum;
    protected readonly statisticService = inject(StatisticService);

    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly exportStatisticsExcelService = inject(ExportStatisticsExcelService);
    private readonly mapModalService = inject(MapModalService);
    private readonly matDialog = inject(MatDialog);
    private observedElement?: HTMLElement;
    private resizeObserver?: ResizeObserver;
    private readonly router = inject(Router);
    private readonly securityService = inject(SecurityService);
    private readonly selectionService = inject(SelectionService);
    private readonly snackBarService = inject(SnackBarService);
    private readonly standardsService = inject(StandardsService);
    private readonly translocoService: TranslocoService = inject(TranslocoService);

    public constructor() {
        this.statisticService.wktStringMultipolygon.set(undefined);

        effect(async () => {
            this.statisticService.isStatisticActive.set(true);

            await this.getOrganizationStandards({
                ...defaultPaginationParameters,
                order: OrderEnum.ASC,
                orderBy: 'name',
                take: paginationMaxTake,
            });
        });

        effect(() => {
            const htmlElement: HTMLElement | undefined = this.headerPanel()?.nativeElement;

            if (!htmlElement) {
                if (this.observedElement) {
                    this.resizeObserver?.unobserve(this.observedElement);
                    this.observedElement = undefined;
                }

                return;
            }

            if (this.observedElement === htmlElement) {
                return;
            }

            const rootFontSize: number = parseFloat(getComputedStyle(document.documentElement).fontSize);
            const thresholdPx: number = 32 * rootFontSize;

            if (!this.resizeObserver) {
                this.resizeObserver = new ResizeObserver(([entry]: ResizeObserverEntry[]): void => {
                    const widthPx = entry.contentRect.width;
                    this.isNarrowPanel.set(widthPx < thresholdPx);
                    this.changeDetectorRef.markForCheck();
                });
            }

            if (this.observedElement) {
                this.resizeObserver.unobserve(this.observedElement);
            }

            this.observeHeaderPanel(htmlElement);
        });

        this.destroyRef.onDestroy(async () => {
            this.statisticService.isStatisticActive.set(false);
            this.geographicFilterService.isStatisticFilterApplied.set(false);
            this.geographicFilterService.isSelectZoneActive.set(false);
            this.hasUnsavedStatisticOrderChanges.set(false);

            this.mapModalService.close(MapModal.FILTER);
            await this.selectionService.clearAllSelections();

            this.disconnectResizeObserver();
        });
    }

    public canDeactivateComponent(): boolean {
        return !this.isEditOrderMode() || !this.hasUnsavedStatisticOrderChanges();
    }

    public confirmDeactivateComponent(): void {
        this.hasUnsavedStatisticOrderChanges.set(false);
        this.isEditOrderMode.set(false);
    }

    public async exportStatisticsAsExcel(): Promise<void> {
        if (this.isExportingExcel()) {
            return;
        }

        this.isExportingExcel.set(true);

        try {
            await this.exportStatisticsExcelService.exportStatistics(
                this.statisticService.computedStatisticList(),
                this.standardNameSelected(),
                this.statisticService.dataVersions(),
            );
        } catch {
            this.snackBarService.openErrorSnackBar('exportFailed');
        } finally {
            this.isExportingExcel.set(false);
        }
    }

    public getComponentDeactivateDialogConfig(): CanDeactivateDialogConfig {
        return {
            cancelLabel: this.translocoService.translate('cancel'),
            confirmLabel: this.translocoService.translate('quit'),
            description: this.translocoService.translate('leavingEditModeWillDiscardChanges'),
            title: this.translocoService.translate('areYouSureToLeaveEditMode'),
            type: ModalType.WARNING,
            width: '30vw',
        };
    }

    public onStandardChange(values: Standard[]): void {
        const standard = values[0];

        if (!standard) {
            return;
        }

        this.statisticService.standardId.set(standard.id);

        this.standardNameSelected.set(standard.name);
        this.isStandardSelected.set(true);
    }

    public openStatsPage(): void {
        if (this.statisticService.wktStringMultipolygon()) {
            localStorage.setItem(
                'exportStatisticsGeographicFilter',
                JSON.stringify({
                    filter: this.statisticService.wktStringMultipolygon()!,
                    type: 'zoneDelimiter',
                } satisfies ZoneDelimiterFilter),
            );
        }
        if (this.statisticService.selectedLayersAndEntities()) {
            localStorage.setItem(
                STATISTICS_FILTER_LOCAL_STORAGE_KEY,
                JSON.stringify({
                    filter: this.statisticService.selectedLayersAndEntities()!,
                    type: 'entitiesSelection',
                } satisfies EntitiesSelectionFilter),
            );
        }

        const url = this.router.serializeUrl(
            this.router.createUrlTree(['/', RoutePathEnum.STATISTICS_EXPORT], {
                queryParams: {
                    geographicFilter: !!this.statisticService.wktStringMultipolygon(),
                    projectId: this.statisticService.projectId(),
                    standardId: this.statisticService.standardId(),
                },
            }),
        );

        window.open(url);
    }

    protected async backToDashboard(): Promise<void> {
        if (!this.isEditOrderMode()) {
            return;
        }

        const confirmed = await this.openStatisticsDeactivateDialog();

        if (!confirmed) {
            return;
        }

        this.confirmDeactivateComponent();
    }

    protected resetStatisticsOrder(): void {
        const dialogRef = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data: {
                cancelLabel: this.translocoService.translate('cancel'),
                confirmLabel: this.translocoService.translate('confirm'),
                description: this.translocoService.translate('exitEditModeToDiscardChanges'),
                title: this.translocoService.translate('confirmResetStatisticsOrder'),
                type: ModalType.INFO,
            },
            width: '30vw',
        });

        dialogRef.afterClosed().subscribe(async (result) => {
            if (result !== ModalResultEnum.CONFIRM) {
                return;
            }
            const userId: string = this.securityService.meSignal()!.id;

            await this.statisticService.updateStatisticsOrder(userId, [], []);

            this.hasUnsavedStatisticOrderChanges.set(false);
            this.isEditOrderMode.set(false);
        });
    }

    protected async saveStatisticOrder(): Promise<void> {
        const newStatisticOrder: string[] = this.statisticService
            .computedStatisticList()
            .map((statistic) => statistic.id);

        await this.statisticService.updateStatisticsOrder(
            this.securityService.meSignal()!.id,
            newStatisticOrder,
            this.statisticService.personStatisticOrder()?.hiddenStatistics ?? [],
        );

        this.hasUnsavedStatisticOrderChanges.set(false);
        this.isEditOrderMode.set(false);

        return;
    }

    protected toggleEditOrderMode(): void {
        this.isEditOrderMode.set(!this.isEditOrderMode());
    }

    private disconnectResizeObserver(): void {
        if (this.observedElement) {
            this.resizeObserver?.unobserve(this.observedElement);
        }

        this.resizeObserver?.disconnect();
        this.resizeObserver = undefined;
        this.observedElement = undefined;
    }

    private async getOrganizationStandards(paginationParameters: PaginationParameters): Promise<void> {
        try {
            this.isStandardAvailable.set(false);
            this.isStandardSelected.set(false);
            this.statisticService.standardId.set('');
            this.standardPerProjectList.set([]);

            const paginatedStandards = await firstValueFrom(
                this.standardsService.standardControllerGet({
                    projectId: this.statisticService.projectId(),
                    ...paginationParameters,
                }),
            );

            this.errorWhileFetchingOrganizationStandards.set(false);
            this.standardPerProjectList.set(paginatedStandards.items);
            this.isLoading.set(false);

            if (paginatedStandards.items.length) {
                this.isStandardAvailable.set(true);
            }

            if (paginatedStandards.items.length === 1) {
                this.onStandardChange([paginatedStandards.items[0]]);
            }
        } catch {
            this.isLoading.set(false);
            this.errorWhileFetchingOrganizationStandards.set(true);
        }
    }

    private observeHeaderPanel(htmlElement: HTMLElement): void {
        this.observedElement = htmlElement;
        this.resizeObserver?.observe(htmlElement);
    }

    private async openStatisticsDeactivateDialog(): Promise<boolean> {
        const { width, ...data } = this.getComponentDeactivateDialogConfig();

        const dialogRef = this.matDialog.open(ConfirmationModal, {
            autoFocus: false,
            data,
            width,
        });

        const result = await firstValueFrom(dialogRef.afterClosed());

        return result === ModalResultEnum.CONFIRM;
    }
}
